import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Handle clicks on menu items
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const menuitem = event.target.closest('[ref="menuitem"]');
      if (!menuitem) return;
      event.stopPropagation();
      this.#debouncedDeactivate.cancel();
      this.#debouncedActivateHandler.cancel();
      const item = menuitem;
      if (item === this.#state.activeItem) {
        this.#deactivate(item);
      } else {
        this.#activateHandler(event);
      }
    }, { signal: this.#abortController.signal });

    // Close menu when clicking outside
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!this.contains(event.target)) {
        this.#deactivate();
      }
    }, { signal: this.#abortController.signal });

    onDocumentLoaded(() => {
      this.#preloadImages();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #state = { activeItem: null };
  #menuData = null;

  #getMenuData() {
    if (this.#menuData) return this.#menuData;
    const el = this.querySelector('script.header-menu-data[type="application/json"]');
    if (!el) return null;
    try {
      this.#menuData = JSON.parse(el.textContent);
      return this.#menuData;
    } catch (err) {
      console.error('[header-menu] Failed to parse menu data JSON:', err);
      return null;
    }
  }

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')?.matches(':hover') ?? false;
  }

  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[ref="menuitem"]');
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.setAttribute('aria-expanded', 'false');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.setAttribute('aria-expanded', 'true');

    let submenu = findSubmenu(item);
    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#maybeLoadSubmenu(submenu);
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    // Show overlay below submenu
    let overlay = document.getElementById('header-menu-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'header-menu-overlay';
      overlay.addEventListener('click', () => this.#deactivate());
      document.body.appendChild(overlay);
    }
    const submenuBottom = this.getBoundingClientRect().bottom + submenuHeight;
    overlay.style.position = 'fixed';
    overlay.style.top = `${submenuBottom}px`;
    overlay.style.left = '0';
    overlay.style.right = '0';
    overlay.style.bottom = '0';
    overlay.style.zIndex = 'calc(var(--layer-header-menu) - 2)';
    overlay.style.display = 'block';
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('[ref="menuitem"]');
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    // Hide overlay
    const overlay = document.getElementById('header-menu-overlay');
    if (overlay) overlay.style.display = 'none';

    const submenu = findSubmenu(item);
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.setAttribute('aria-expanded', 'false');
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu) delete submenu.dataset.active;
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #maybeLoadSubmenu(submenuEl) {
    const grid = submenuEl.querySelector('.mega-menu__grid[data-submenu-handle]');
    if (!grid || grid.children.length > 0) return;

    const handle = grid.dataset.submenuHandle;
    const allItems = this.#getMenuData();
    if (!allItems) return;

    const item = allItems.find(i => i.handle === handle);
    if (!item?.children?.length) return;

    const ul = document.createElement('ul');
    ul.className = 'mega-menu__list list-unstyled';
    ul.style.setProperty('--menu-image-border-radius', '0px');

    const li = document.createElement('li');
    li.className = 'mega-menu__column mega-menu__column--span-1';

    item.children.forEach(child => {
      const wrapper = document.createElement('div');
      const a = document.createElement('a');
      a.href = child.url;
      a.className = 'mega-menu__link';
      const span = document.createElement('span');
      span.className = 'mega-menu__link-title wrap-text';
      span.textContent = child.title;
      a.appendChild(span);
      wrapper.appendChild(a);
      li.appendChild(wrapper);
    });

    ul.appendChild(li);
    grid.appendChild(ul);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}